import { createContext, useContext, useState, useCallback, ReactNode } from "react";

type PromptPriority = "proximity" | "incomplete-vibe" | "post-visit";

const PRIORITY_ORDER: PromptPriority[] = ["proximity", "incomplete-vibe", "post-visit"];

interface BottomPromptSlotContextValue {
  register: (id: PromptPriority) => void;
  unregister: (id: PromptPriority) => void;
  isVisible: (id: PromptPriority) => boolean;
}

const BottomPromptSlotContext = createContext<BottomPromptSlotContextValue>({
  register: () => {},
  unregister: () => {},
  isVisible: () => true,
});

export function useBottomPromptSlot() {
  return useContext(BottomPromptSlotContext);
}

export function BottomPromptSlotProvider({ children }: { children: ReactNode }) {
  const [activePrompts, setActivePrompts] = useState<Set<PromptPriority>>(new Set());

  const register = useCallback((id: PromptPriority) => {
    setActivePrompts((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unregister = useCallback((id: PromptPriority) => {
    setActivePrompts((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const isVisible = useCallback(
    (id: PromptPriority) => {
      // The highest-priority active prompt wins
      for (const p of PRIORITY_ORDER) {
        if (activePrompts.has(p)) return p === id;
      }
      return false;
    },
    [activePrompts]
  );

  return (
    <BottomPromptSlotContext.Provider value={{ register, unregister, isVisible }}>
      {children}
    </BottomPromptSlotContext.Provider>
  );
}
